import { SearchDisplayAction } from '../components/search-display-action.tsx'
import { searchDisplayIntent } from '../lib/search-display-intent.ts'
import { searchManageAlerts } from '../lib/search-manage-alerts.ts'
import { useSearchDateRange } from '../hooks/use-search-date-range.ts'
import { scopedSearchResult } from '../lib/search-report-scope.ts'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Fragment, useEffect, useMemo, useRef, useState, type JSX } from 'react'

import { useSearchAlerts } from '../hooks/use-page-alert.ts'
import { searchAlertIntent } from '../lib/search-alert-intent.ts'
import { SearchAskResults } from '../components/search-ask-results.tsx'
import { useLandingNavigation } from '../components/landing-navigation.tsx'
import { Button } from '../design-system/button.tsx'
import { EmptyState } from '../design-system/empty-state.tsx'
import { Input } from '../design-system/input.tsx'
import { Icon } from '../design-system/icon.tsx'
import { LINK_CLASS, Link } from '../design-system/link.tsx'
import { Notice } from '../design-system/notice.tsx'
import { Panel } from '../design-system/panel.tsx'
import { useMerchants } from '../hooks/use-merchants.ts'
import { figureHref } from '../lib/figure-focus.ts'
import {
  findQueryMerchant,
  searchMerchants,
  merchantReportHref
} from '../lib/merchant-search.ts'
import {
  merchantLabel,
  UNNAMED_MERCHANT,
  type Merchant
} from '../lib/merchants.ts'
import { usePeopleSearch } from '../hooks/use-people-search.ts'
import {
  looksLikePerson,
  personDetail,
  personHref,
  type PeoplePaths,
  type PeopleSearchAnswer
} from '../lib/people-search.ts'
import {
  rankPages,
  type PageSearchField,
  type RankedPage
} from '../lib/page-search.ts'
import { searchExamples, shuffleExamples } from '../lib/search-examples.ts'
import { normalizeAskQuery, suggestedPages } from '../lib/search-ask.ts'
import { recordSearchOpened } from '../lib/search-opened.ts'
import { useSearchAsk } from '../hooks/use-search-ask.ts'
import type { InternalPage } from '../lib/pages.ts'

export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): { q?: string } => {
    // The router parses bare numbers and booleans as JSON, including merchant ids.
    const q =
      typeof search.q === 'number' || typeof search.q === 'boolean'
        ? String(search.q)
        : search.q
    return { q: typeof q === 'string' && q.trim() !== '' ? q : undefined }
  },
  component: LandingPage
})

const MATCH_LABELS: Record<PageSearchField, string> = {
  title: 'in title',
  keywords: 'matched keyword',
  figures: 'matched figure',
  section: 'in section',
  description: 'in description',
  id: 'matched id'
}

function resultLabel(result: RankedPage): string {
  if (result.matchedField === 'figures' && result.matchedFigure !== null) {
    return `shows ${result.matchedFigure}`
  }
  return MATCH_LABELS[result.matchedField]
}

const RESULT_ROW_CLASS =
  'border-border hover:bg-accent flex flex-col gap-1 border p-4'

const SHORTCUT_CARD_CLASS = `${RESULT_ROW_CLASS} bg-card h-full`

function countLabel(count: number, singular: string): string {
  return `${count} ${count === 1 ? singular : `${singular}s`}`
}

/** A query the route drops, because trimming it leaves nothing. */
function isBlankQuery(value: string): boolean {
  return value.trim() === ''
}

/**
 * What the live status says while a query is showing.
 *
 * Merchants are named only after a search this viewer can run has settled.
 * A closed Merchant 360 path, a pending directory, and a failed directory
 * are not zero merchants.
 */
function searchStatus(
  pageCount: number,
  merchants: {
    searchable: boolean
    pending: boolean
    failed: boolean
    count: number
  }
): string {
  const pages = countLabel(pageCount, 'page')
  if (!merchants.searchable || merchants.failed) return pages
  if (merchants.pending) return `${pages}, searching merchants`
  return `${pages} and ${countLabel(merchants.count, 'merchant')}`
}

/**
 * The typed draft stays ahead of the URL.
 *
 * Copy `q` during render when it is the value just sent, or the reader has
 * not typed since the last commit. A blank draft and a blank query match,
 * because the route drops a whitespace-only `q`. A late commit must not wipe
 * a newer draft. An effect must not set this state. Each accepted outside
 * query bumps a counter, so a repeat of the same query focuses the field.
 */
function SearchField({
  q,
  onQuery,
  onAsk
}: {
  q: string
  onQuery: (next: string) => void
  /** Enter sends the typed question to the AI. Typing alone never does. */
  onAsk: (question: string) => void
}): JSX.Element {
  const [draft, setDraft] = useState(q)
  const [prevQ, setPrevQ] = useState(q)
  const [sent, setSent] = useState(q)
  const [focusTick, setFocusTick] = useState(0)
  const field = useRef<HTMLInputElement>(null)
  if (q !== prevQ) {
    const urlCaughtUp = q === sent || (isBlankQuery(q) && isBlankQuery(sent))
    const readerHasNotTyped =
      draft === prevQ || (isBlankQuery(draft) && isBlankQuery(prevQ))
    const nextDraft = urlCaughtUp || readerHasNotTyped ? q : draft
    if (nextDraft !== draft) setFocusTick(focusTick + 1)
    if (nextDraft === q) setSent(q)
    setPrevQ(q)
    setDraft(nextDraft)
  }
  useEffect(() => {
    if (focusTick === 0) return
    field.current?.focus()
  }, [focusTick])

  return (
    <Input
      ref={field}
      data-testid="search-page-input"
      label="Ask a question"
      placeholder="Ask a question, or say “alert me when…”"
      hint="Press Enter and AI finds the reports that answer your question, and people are found by name or email. Pages and merchants match as you type."
      autoFocus
      className="h-14 text-lg md:text-lg"
      value={draft}
      onChange={event => {
        const next = event.target.value
        setSent(next)
        setDraft(next)
        onQuery(next)
      }}
      onKeyDown={event => {
        if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
        event.preventDefault()
        onAsk(draft)
      }}
    />
  )
}

function SearchResultRow({
  testId,
  href,
  title,
  detail,
  body,
  meta,
  merchant,
  alertPath,
  onOpen
}: {
  testId: string
  href: string
  title: string
  detail?: string
  body?: string
  meta?: string
  merchant?: Merchant | null
  alertPath?: string
  /** Runs after navigation starts, so it never holds the reader. */
  onOpen?: () => void
}): JSX.Element {
  const router = useRouter()
  const alerts = useSearchAlerts()
  return (
    <li>
      <Link
        data-testid={testId}
        href={href}
        onNavigate={() => {
          router.history.push(href)
          if (alertPath !== undefined) alerts?.openAlertOn(alertPath)
          onOpen?.()
        }}
        className={RESULT_ROW_CLASS}
      >
        <span className={`${LINK_CLASS} flex items-center gap-2 font-medium`}>
          {alertPath !== undefined ? (
            <Icon
              name="bell"
              data-testid={`${testId}-alert-icon`}
              className="size-4 shrink-0"
            />
          ) : null}
          {title}
        </span>
        {alertPath !== undefined ? (
          <span
            data-testid={`${testId}-alert-hint`}
            className="text-muted-foreground text-sm"
          >
            Opens the alert setup on this report
          </span>
        ) : null}
        {detail !== undefined ? (
          <span className="text-muted-foreground text-sm">{detail}</span>
        ) : null}
        {merchant ? (
          <span
            data-testid={`${testId}-merchant`}
            className="text-muted-foreground text-sm"
          >
            for {merchantLabel(merchant)}
          </span>
        ) : null}
        {body !== undefined ? <span className="text-sm">{body}</span> : null}
        {meta !== undefined ? (
          <span className="text-muted-foreground text-xs">{meta}</span>
        ) : null}
      </Link>
    </li>
  )
}

/** Where a merchant opens, and what its row calls that. */
interface MerchantDestination {
  title: string
  href: (orgId: number) => string
}

function MerchantResults({
  destination,
  merchants
}: {
  destination: MerchantDestination
  merchants: readonly Merchant[]
}): JSX.Element {
  return (
    <section data-testid="search-page-merchants">
      <h2 className="type-section-title">Merchants</h2>
      <ul className="mt-4 grid gap-4">
        {merchants.map(merchant => (
          <SearchResultRow
            key={merchant.orgId}
            testId={`search-page-merchant-${merchant.orgId}`}
            href={destination.href(merchant.orgId)}
            title={destination.title}
            detail={merchantLabel(merchant)}
            meta={String(merchant.orgId)}
          />
        ))}
      </ul>
    </section>
  )
}

function PeopleResults({
  paths,
  answer
}: {
  paths: PeoplePaths
  answer: PeopleSearchAnswer
}): JSX.Element | null {
  if (answer.people.length === 0) return null
  return (
    <section data-testid="search-page-people">
      <h2 className="type-section-title">People</h2>
      <ul className="mt-4 grid gap-4">
        {answer.people.map(person => (
          <SearchResultRow
            key={person.id}
            testId={`search-page-person-${person.id}`}
            href={personHref(paths, person)}
            title={person.name ?? person.email ?? 'Unnamed person'}
            detail={personDetail(person, person.merchant ?? UNNAMED_MERCHANT)}
            meta={person.email ?? undefined}
          />
        ))}
      </ul>
      {answer.capped ? (
        <p
          data-testid="search-page-people-capped"
          className="text-muted-foreground mt-2 text-sm"
        >
          More people match. Type more of the name or the email.
        </p>
      ) : null}
    </section>
  )
}

/** Search the pages this employee may open, with a shareable query. */
function LandingPage(): JSX.Element {
  const router = useRouter()
  const { q = '' } = Route.useSearch()
  const navigate = Route.useNavigate()
  const searching = q.trim() !== ''
  const displayIntent = searchDisplayIntent(q)
  const intent = searchAlertIntent(displayIntent?.topic ?? q)
  const date = useSearchDateRange(intent.topic)
  const topic = date.topic.trim()
  const alerts = useSearchAlerts()
  const alertReports = alerts?.reports
  const alertPaths = useMemo(
    () => new Set(alertReports?.map(report => report.path)),
    [alertReports]
  )
  const {
    sections,
    pages,
    starredPages,
    recentPages,
    openCustomization,
    merchant360Path
  } = useLandingNavigation()

  const management = searchManageAlerts(q, pages)
  const [exampleOrder] = useState(() => shuffleExamples())
  const examples = searchExamples(exampleOrder, pages, 6)
  // A query that arrives from outside the field (a shared link, the palette's
  // see-all row, an example) is a whole question, so it is asked at once.
  // A typed query waits for Enter, so a half-typed word never reaches the AI.
  const [asked, setAsked] = useState(q)
  const [typed, setTyped] = useState<string | null>(null)
  const [prevQ, setPrevQ] = useState(q)
  if (q !== prevQ) {
    setPrevQ(q)
    const fromField =
      typed !== null &&
      (q === typed || (isBlankQuery(q) && isBlankQuery(typed)))
    if (!fromField) setAsked(q)
  }
  const askReady = normalizeAskQuery(asked) === normalizeAskQuery(q)
  function onQuery(next: string): void {
    setTyped(next)
    void navigate({ search: { q: next || undefined }, replace: true })
  }
  function onAsk(question: string): void {
    setAsked(question)
  }
  const customerListPath =
    pages.find(entry => entry.page.id === 'customer-list')?.page.path ?? null
  // Merchant 360 when the reader may open it, else that merchant's Customer list.
  const merchantDestination: MerchantDestination | null =
    merchant360Path !== null
      ? {
          title: 'Open Merchant 360',
          href: orgId => merchantReportHref(merchant360Path, '', orgId)
        }
      : customerListPath !== null
        ? {
            title: 'Open Customer list',
            href: orgId => `${customerListPath}?merchantId=${orgId}`
          }
        : null
  const canSearchMerchants =
    searching && merchantDestination !== null && management === null
  // A name lookup scans every customer, so it waits for Enter like the AI.
  const peopleQuery =
    askReady &&
    searching &&
    management === null &&
    !intent.alert &&
    customerListPath !== null &&
    looksLikePerson(q)
      ? q
      : ''
  const people = usePeopleSearch(peopleQuery)
  const peoplePending = peopleQuery !== '' && people.isPending
  const peopleFailed = peopleQuery !== '' && people.isError
  const peopleCount = peopleQuery !== '' ? (people.data?.people.length ?? 0) : 0
  const directory = useMerchants({ enabled: canSearchMerchants })
  const directoryMerchants = directory.isSuccess
    ? directory.data.merchants
    : undefined
  const queryMerchant = useMemo(
    () =>
      canSearchMerchants && directoryMerchants !== undefined
        ? findQueryMerchant(directoryMerchants, topic, pages)
        : null,
    [canSearchMerchants, directoryMerchants, topic, pages]
  )
  const pageQuery = queryMerchant?.rest || topic
  const results =
    intent.alert && topic === '' ? [] : rankPages(pages, pageQuery)
  // Share one request with SearchAskResults; an off answer is deliberately not cached.
  const ask = useSearchAsk(management || !askReady ? '' : date.topic)
  // A keyword row Jev also suggested records as a Jev open, at the AI's rank, as the palette does.
  const suggestedIds =
    ask.status === 'ready'
      ? suggestedPages(ask.answer, pages).map(entry => entry.page.id)
      : []
  const merchantsPending = canSearchMerchants && directory.isPending
  const merchantsFailed = canSearchMerchants && directory.isError
  const merchants =
    canSearchMerchants && directory.data !== undefined
      ? searchMerchants(directory.data.merchants, topic)
      : []
  const showEmpty =
    results.length === 0 &&
    merchants.length === 0 &&
    !merchantsPending &&
    !merchantsFailed &&
    peopleCount === 0 &&
    !peoplePending &&
    !peopleFailed

  function shortcuts(
    title: string,
    shortcutsPages: readonly InternalPage[]
  ): JSX.Element | null {
    if (shortcutsPages.length === 0) return null
    const kind = title.toLowerCase()
    return (
      <Panel data-testid={`landing-${kind}`} title={title} width="wide">
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shortcutsPages.map(page => (
            <li key={page.id}>
              <Link
                data-testid={`landing-${kind}-${page.id}`}
                href={page.path}
                onNavigate={() => router.history.push(page.path)}
                className={SHORTCUT_CARD_CLASS}
                aria-label={page.title}
                aria-describedby={`landing-${kind}-description-${page.id}`}
              >
                <span className={`${LINK_CLASS} text-sm font-medium`}>
                  {page.title}
                </span>
                <span
                  id={`landing-${kind}-description-${page.id}`}
                  className="text-muted-foreground text-sm"
                >
                  {page.description}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Panel>
    )
  }

  return (
    <div className="grid gap-10">
      <div className="mx-auto grid w-full max-w-3xl gap-6 pt-[10vh]">
        <SearchField q={q} onQuery={onQuery} onAsk={onAsk} />
        {!searching && examples.length > 0 ? (
          <section data-testid="landing-examples" className="grid gap-3">
            <h2 className="type-section-title text-center">Try asking</h2>
            <ul className="flex flex-wrap justify-center gap-3">
              {examples.map(example => (
                <li key={example.pageId}>
                  <Button
                    data-testid={`landing-example-${example.pageId}`}
                    variant="outline"
                    className="h-auto whitespace-normal text-left"
                    onClick={() => {
                      onQuery(example.question)
                      onAsk(example.question)
                    }}
                  >
                    {example.question}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
      <p
        data-testid="search-page-status"
        role="status"
        aria-live="polite"
        className="sr-only"
      >
        {searching && !management
          ? searchStatus(results.length, {
              searchable: canSearchMerchants,
              pending: merchantsPending,
              failed: merchantsFailed,
              count: merchants.length
            })
          : ''}
      </p>
      {management ? (
        <Panel data-testid="landing-page" title="Search results" width="wide">
          <Link
            data-testid="search-page-manage-alerts"
            href={management.href}
            onNavigate={() => router.history.push(management.href)}
            className={RESULT_ROW_CLASS}
          >
            <span className={LINK_CLASS}>{management.label}</span>
            <span>{management.description}</span>
          </Link>
        </Panel>
      ) : searching ? (
        <>
          <SearchAskResults
            query={q}
            asked={askReady}
            ask={ask}
            pages={pages}
            queryMerchant={queryMerchant}
            keywordResults={results}
            emptyKeywordResults={
              showEmpty ? (
                <Panel
                  data-testid="landing-page"
                  title="Search results"
                  width="wide"
                >
                  <EmptyState
                    data-testid="search-page-empty"
                    title="No matches"
                    description="Try another name, keyword, section, or description."
                  />
                </Panel>
              ) : null
            }
          />
          {!showEmpty ? (
            <Panel
              data-testid="landing-page"
              title="Search results"
              width="wide"
            >
              {results.length > 0 ? (
                <ul data-testid="search-page-results" className="grid gap-4">
                  {results.map((result, index) => {
                    const alertRow =
                      intent.alert && alertPaths.has(result.page.path)
                    const scope = scopedSearchResult(
                      result.page,
                      date,
                      result.page.takesMerchants
                        ? queryMerchant?.merchant
                        : null
                    )
                    return (
                      <Fragment key={result.page.id}>
                        <SearchResultRow
                          key={result.page.id}
                          testId={`search-page-result-${result.page.id}`}
                          href={figureHref(
                            scope.href,
                            result.matchedField === 'figures'
                              ? result.matchedFigure
                              : null
                          )}
                          merchant={
                            result.page.takesMerchants
                              ? queryMerchant?.merchant
                              : null
                          }
                          alertPath={alertRow ? result.page.path : undefined}
                          onOpen={
                            ask.status === 'ready'
                              ? () => {
                                  const aiRank =
                                    suggestedIds.indexOf(result.page.id) + 1
                                  recordSearchOpened({
                                    query: date.topic,
                                    pageId: result.page.id,
                                    rank: aiRank > 0 ? aiRank : index + 1,
                                    source: aiRank > 0 ? 'jev' : 'keyword'
                                  })
                                }
                              : undefined
                          }
                          title={
                            (alertRow
                              ? `Set up an alert · ${result.page.title}`
                              : result.page.title) + scope.label
                          }
                          detail={result.group}
                          body={result.page.description}
                          meta={resultLabel(result)}
                        />
                        {displayIntent ? (
                          <SearchDisplayAction
                            query={q}
                            entry={result}
                            pages={pages}
                            mode="keyword"
                            onNavigate={href => router.history.push(href)}
                          />
                        ) : null}
                      </Fragment>
                    )
                  })}
                </ul>
              ) : null}
              {merchantsPending ? (
                <Notice
                  data-testid="search-page-merchants-pending"
                  tone="info"
                  announce={false}
                >
                  Searching merchants…
                </Notice>
              ) : null}
              {merchantsFailed ? (
                <Notice
                  data-testid="search-page-merchants-error"
                  tone="warning"
                >
                  Merchants are unavailable right now.
                </Notice>
              ) : null}
              {merchantDestination !== null && merchants.length > 0 ? (
                <MerchantResults
                  destination={merchantDestination}
                  merchants={merchants}
                />
              ) : null}
              {peoplePending ? (
                <Notice
                  data-testid="search-page-people-pending"
                  tone="info"
                  announce={false}
                >
                  Searching people…
                </Notice>
              ) : null}
              {peopleFailed ? (
                <Notice data-testid="search-page-people-error" tone="warning">
                  People search is unavailable right now.
                </Notice>
              ) : null}
              {customerListPath !== null &&
              peopleQuery !== '' &&
              people.data !== undefined ? (
                <PeopleResults
                  paths={{
                    customerList: customerListPath,
                    merchantUsers:
                      pages.find(entry => entry.page.id === 'merchant-users')
                        ?.page.path ?? null
                  }}
                  answer={people.data}
                />
              ) : null}
            </Panel>
          ) : null}
        </>
      ) : (
        <>
          {shortcuts('Starred', starredPages)}
          {shortcuts('Recent', recentPages)}
          <Panel
            data-testid="landing-page"
            title="All pages"
            description="Choose a section to open a page."
            width="wide"
            action={
              <Button
                data-testid="landing-customize"
                variant="outline"
                onClick={openCustomization}
              >
                Customize
              </Button>
            }
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sections.map(section => {
                const slug = section.group.toLowerCase().replace(/\s+/g, '-')

                return (
                  <section
                    key={section.group}
                    data-testid={`landing-section-${slug}`}
                    className="border-border bg-card border p-4"
                  >
                    <div
                      data-testid={`landing-section-${slug}-heading`}
                      className="flex items-baseline justify-between gap-2"
                    >
                      <h2 className="type-section-title">{section.group}</h2>
                    </div>
                    <ul className="mt-4 flex flex-col gap-3">
                      {section.pages.map(page => (
                        <li key={page.id} className="flex flex-col gap-1">
                          <Link
                            data-testid={`landing-link-${page.id}`}
                            href={page.path}
                            onNavigate={() => router.history.push(page.path)}
                            className={`${LINK_CLASS} text-sm font-medium`}
                            aria-describedby={`landing-description-${page.id}`}
                          >
                            {page.title}
                          </Link>
                          <p
                            id={`landing-description-${page.id}`}
                            data-testid={`landing-description-${page.id}`}
                            className="text-muted-foreground text-sm"
                          >
                            {page.description}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </section>
                )
              })}
            </div>
          </Panel>
        </>
      )}
    </div>
  )
}
